// Defensive normalizers for the analytics chart payloads.
//
// The Activity APIs may return rows either as arrays of objects, plain
// {label: value} maps, or arrays of [label, value] pairs. These helpers coerce
// the common shapes into the [label, number] tuples the charts expect, so the
// UI stays resilient to small backend variations.

// Pull the first defined value among candidate keys.
const pick = (obj, keys) => {
    for (const k of keys) {
        if (obj && obj[k] !== undefined && obj[k] !== null) return obj[k];
    }
    return undefined;
};

const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

// Generic: turn an array|object payload into [{label, value}] using key hints.
const toSeries = (raw, labelKeys, valueKeys) => {
    if (!raw) return [];

    if (Array.isArray(raw)) {
        return raw
            .map((row) => {
                if (Array.isArray(row)) {
                    return {label: String(row[0] ?? ""), value: num(row[1])};
                }
                if (row && typeof row === "object") {
                    return {
                        label: String(pick(row, labelKeys) ?? ""),
                        value: num(pick(row, valueKeys)),
                    };
                }
                return null;
            })
            .filter(Boolean);
    }

    if (typeof raw === "object") {
        return Object.entries(raw).map(([label, v]) => ({
            label: String(label),
            value:
                v && typeof v === "object" ? num(pick(v, valueKeys)) : num(v),
        }));
    }

    return [];
};

export const dailyRevenueSeries = (summary) =>
    toSeries(
        summary?.daily_timeline,
        ["date", "day", "label"],
        ["revenue", "revenue_ttc", "total", "amount", "value"]
    );

export const dailyOrdersSeries = (summary) =>
    toSeries(
        summary?.daily_timeline,
        ["date", "day", "label"],
        ["orders_count", "orders", "count", "value"]
    );

export const paymentMethodsSeries = (summary) =>
    toSeries(
        summary?.payments,
        ["method", "label", "name", "type"],
        ["amount", "total", "revenue", "value", "sum"]
    );

export const revenueByHourSeries = (performance) =>
    toSeries(
        performance?.by_hour,
        ["hour", "label", "time"],
        ["revenue", "total", "amount", "value"]
    );

export const revenueByPeriodSeries = (performance) =>
    toSeries(
        performance?.by_period,
        ["period", "label", "name", "service"],
        ["revenue", "total", "amount", "value"]
    );

export const frequencyDistributionSeries = (retention) =>
    toSeries(
        retention?.frequency_distribution,
        ["label", "orders", "bucket"],
        ["clients", "count", "value"]
    );

// New vs returning → {new, returning} each {clients, orders, revenue}.
export const newVsReturning = (retention) => {
    const nvr = retention?.new_vs_returning;
    if (!nvr || typeof nvr !== "object") return null;
    const part = (key) => {
        const p = nvr[key] || {};
        return {
            clients: num(pick(p, ["clients", "count"])),
            orders: num(pick(p, ["orders", "orders_count"])),
            revenue: num(pick(p, ["revenue", "total", "amount"])),
        };
    };
    return {new: part("new"), returning: part("returning")};
};

// Top clients → normalized rows for the table widget.
export const topClientsRows = (retention) => {
    const rows = retention?.top_clients;
    if (!Array.isArray(rows)) return [];
    return rows.map((r) => ({
        id: pick(r, ["client_id", "id"]),
        name: pick(r, ["name", "client_name", "full_name"]) || "—",
        phone: pick(r, ["phone", "telephone", "tel"]) || "",
        orders: num(pick(r, ["orders_count", "orders", "count"])),
        revenue: num(pick(r, ["revenue", "total", "amount"])),
        aov: num(pick(r, ["average_order_value", "aov", "avg_order_value"])),
    }));
};

// Heatmap → matrix of {weekday, hour, value}. Accepts array of objects or a
// nested {weekday: {hour: value}} map.
export const heatmapMatrix = (performance) => {
    const raw = performance?.weekday_hourly;
    if (!raw) return [];

    if (Array.isArray(raw)) {
        return raw
            .map((row) => {
                if (!row || typeof row !== "object") return null;
                return {
                    weekday: pick(row, ["weekday", "day", "dow", "day_of_week"]),
                    hour: num(pick(row, ["hour", "h"])),
                    value: num(pick(row, ["revenue", "orders_count", "count", "value", "total"])),
                };
            })
            .filter((r) => r && r.weekday !== undefined && r.weekday !== null);
    }

    if (typeof raw === "object") {
        const out = [];
        Object.entries(raw).forEach(([weekday, hours]) => {
            if (hours && typeof hours === "object") {
                Object.entries(hours).forEach(([hour, value]) => {
                    out.push({weekday, hour: num(hour), value: num(value)});
                });
            }
        });
        return out;
    }

    return [];
};
